import {
  Layers,
  Mesh,
  MeshBasicMaterial,
  MeshDepthMaterial,
  NoBlending,
  OrthographicCamera,
  PlaneGeometry,
  RGBADepthPacking,
  Scene,
  ShaderMaterial,
  WebGLRenderer,
  WebGLRenderTarget,
} from 'three';
import { HorizontalBlurShader } from 'three/addons/shaders/HorizontalBlurShader.js';
import { VerticalBlurShader } from 'three/addons/shaders/VerticalBlurShader.js';

/**
 * Layer index that shadow casters (cards) opt into. Anything on this layer
 * shows up in the shadow render pass; the main camera renders the default
 * layer (0) and ignores it. Caller is responsible for marking card meshes:
 *   group.traverse((c) => c.layers.enable(SHADOW_CASTER_LAYER));
 */
export const SHADOW_CASTER_LAYER = 1;

export interface ShadowArea {
  width: number;
  depth: number;
  centerX?: number;
  centerZ?: number;
}

export interface ShadowPassOptions {
  renderer: WebGLRenderer;
  scene: Scene;
  area: ShadowArea;
  resolution?: number;  // RT side length, default 1024
  height?: number;      // ortho far plane (above table), default 4
  blur?: number;        // gaussian radius in RT pixels, default 2.5
  darkness?: number;    // alpha multiplier, default 1.0
  opacity?: number;     // shadow plane opacity, default 0.85
  y?: number;           // y position of shadow plane above felt, default 0.001
}

export interface ShadowPass {
  plane: Mesh;
  update(): void;
  setArea(area: ShadowArea): void;
  setOpacity(opacity: number): void;
  setBlur(blur: number): void;
  setDarkness(darkness: number): void;
  dispose(): void;
}

/**
 * Render-to-texture contact shadow: top-down orthographic depth render of all
 * cards, two-pass separable Gaussian blur, composited onto a single ground
 * plane. Replaces per-card shadow blobs with one unified silhouette - stacks
 * collapse into a single coherent soft shape instead of accumulating ugly
 * per-card smears.
 *
 * Pattern lifted from drei's <ContactShadows> / three.js webgl_shadow_contact.
 */
export function createShadowPass(opts: ShadowPassOptions): ShadowPass {
  const { renderer, scene } = opts;
  const resolution = opts.resolution ?? 1024;
  let blur = opts.blur ?? 2.5;
  let darkness = opts.darkness ?? 1.0;
  const planeY = opts.y ?? 0.001;

  const rt = new WebGLRenderTarget(resolution, resolution);
  rt.texture.generateMipmaps = false;
  const rtBlur = new WebGLRenderTarget(resolution, resolution);
  rtBlur.texture.generateMipmaps = false;

  // Shadow plane covers the requested area; we update its scale + position via setArea.
  const planeGeom = new PlaneGeometry(1, 1);
  const planeMat = new MeshBasicMaterial({
    map: rt.texture,
    transparent: true,
    opacity: opts.opacity ?? 0.85,
    depthWrite: false,
  });
  const plane = new Mesh(planeGeom, planeMat);
  plane.rotation.x = -Math.PI / 2;
  plane.scale.y = -1; // flip V so the top-down render isn't mirrored on the felt
  plane.position.y = planeY;
  plane.renderOrder = 2;

  // Camera sees only the shadow-caster layer (cards). Positioned above the
  // table looking straight down; near plane at the top, far plane at the felt.
  const cam = new OrthographicCamera();
  cam.layers = new Layers();
  cam.layers.set(SHADOW_CASTER_LAYER);

  // Override material outputs alpha based on depth: cards closer to the felt
  // (far plane) cast the strongest shadow, cards lifted into the air (near the
  // top) cast a weaker shadow. That recession is exactly the cue we want - a
  // dragged card's shadow should fall away as it lifts.
  // Default depth test stays on, so for a stack of N coplanar cards only the
  // topmost wins and shadow density doesn't multiply with stack depth.
  const depthMat = new MeshDepthMaterial({
    depthPacking: RGBADepthPacking,
    blending: NoBlending,
  });
  const darknessUniform = { value: darkness };
  depthMat.userData.darknessUniform = darknessUniform;
  depthMat.onBeforeCompile = (shader) => {
    shader.uniforms.darkness = darknessUniform;
    // smoothstep density curve (instead of linear fragCoordZ): every card
    // close to the felt - including 20-card waste stacks - gets the same
    // anchored full-strength shadow. Only meaningfully airborne cards (drag,
    // win cascade) fade. Without this the stack's shadow lightens as it
    // grows, which reads as "the pile is floating" instead of "thick book
    // resting on the table."
    //   fragCoordZ ≥ 0.92 (worldY ≲ 0.24): full darkness
    //   fragCoordZ ≤ 0.60 (worldY ≳ 1.20): no shadow
    //   between: smooth fade
    shader.fragmentShader = `uniform float darkness;\n${shader.fragmentShader.replace(
      'gl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );',
      'gl_FragColor = vec4( vec3( 0.0 ), smoothstep( 0.60, 0.92, fragCoordZ ) * darkness );',
    )}`;
  };

  // Fullscreen blur quad - uses an ortho camera that maps a unit plane to the viewport.
  const blurScene = new Scene();
  const blurCam = new OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 1);
  const horizontalBlurMat = new ShaderMaterial({ ...HorizontalBlurShader });
  horizontalBlurMat.depthTest = false;
  const verticalBlurMat = new ShaderMaterial({ ...VerticalBlurShader });
  verticalBlurMat.depthTest = false;
  const blurQuad = new Mesh(new PlaneGeometry(1, 1), horizontalBlurMat);
  blurScene.add(blurQuad);

  function setArea(area: ShadowArea) {
    const cx = area.centerX ?? 0;
    const cz = area.centerZ ?? 0;
    const halfW = area.width / 2;
    const halfD = area.depth / 2;
    const far = opts.height ?? 4;
    cam.left = -halfW;
    cam.right = halfW;
    cam.top = halfD;
    cam.bottom = -halfD;
    cam.near = 0;
    cam.far = far;
    cam.position.set(cx, far, cz);
    cam.lookAt(cx, 0, cz);
    cam.updateProjectionMatrix();
    plane.position.set(cx, planeY, cz);
    plane.scale.set(area.width, -area.depth, 1);
  }
  setArea(opts.area);

  function blurPass(amount: number) {
    blurQuad.material = horizontalBlurMat;
    horizontalBlurMat.uniforms.tDiffuse!.value = rt.texture;
    horizontalBlurMat.uniforms.h!.value = amount / resolution;
    renderer.setRenderTarget(rtBlur);
    renderer.render(blurScene, blurCam);

    blurQuad.material = verticalBlurMat;
    verticalBlurMat.uniforms.tDiffuse!.value = rtBlur.texture;
    verticalBlurMat.uniforms.v!.value = amount / resolution;
    renderer.setRenderTarget(rt);
    renderer.render(blurScene, blurCam);
  }

  function update() {
    // Render only the shadow caster layer with the depth override material.
    const prevOverride = scene.overrideMaterial;
    const prevBackground = scene.background;
    const prevAutoClear = renderer.autoClear;
    const prevTarget = renderer.getRenderTarget();

    // Hide the shadow plane itself so it doesn't self-render.
    const planeWasVisible = plane.visible;
    plane.visible = false;

    scene.overrideMaterial = depthMat;
    scene.background = null;
    darknessUniform.value = darkness;

    renderer.autoClear = true;
    renderer.setRenderTarget(rt);
    renderer.setClearColor(0x000000, 0);
    renderer.clear();
    renderer.render(scene, cam);

    scene.overrideMaterial = prevOverride;

    blurPass(blur);
    blurPass(blur * 0.4);

    scene.background = prevBackground;
    renderer.autoClear = prevAutoClear;
    renderer.setRenderTarget(prevTarget);
    plane.visible = planeWasVisible;
  }

  return {
    plane,
    update,
    setArea,
    setOpacity(opacity) {
      planeMat.opacity = opacity;
    },
    setBlur(b) {
      blur = b;
    },
    setDarkness(d) {
      darkness = d;
    },
    dispose() {
      rt.dispose();
      rtBlur.dispose();
      planeGeom.dispose();
      planeMat.dispose();
      depthMat.dispose();
      horizontalBlurMat.dispose();
      verticalBlurMat.dispose();
      blurQuad.geometry.dispose();
    },
  };
}
